import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AppLayout from '../components/AppLayout/AppLayout';
import AllAppsLayout from '../components/AllAppsLayout/AllAppsLayout';
import AccessGate from '../components/AccessGate';

import PayrollTaxes from '../pages/PayrollTaxes';
import ArchivedForms from '../pages/ArchivedForms';
import PD7AWorksheet from '../pages/PD7AWorksheet';
import PaymentHistory from '../pages/PaymentHistory';
import AuditLog from '../pages/AuditLog';
import PayrollTaxPrintView from '../pages/PayrollTaxPrintView';
import T4EmployerSlips from '../pages/T4EmployerSlips';
import T4Summary from '../pages/T4Summary';
import T4EmployeeSlips from '../pages/T4EmployeeSlips';
import Compliance from '../pages/Compliance';
import PayRunDetail from '../pages/PayRunDetail';
import PayStubDetail from '../pages/PayStubDetail';
import EmployeesList from '../pages/EmployeesList';
import EmployeesDirectoryPage from '../pages/EmployeesDirectoryPage';
import PayrollItems from '../pages/PayrollItems';
import PaySchedule from '../pages/PaySchedule';
import WorkLocation from '../pages/WorkLocation';
import EmployeeProfileV2 from '../pages/EmployeeProfileV2';
import AddEmployee from '../pages/AddEmployee';
import PayrollOverview from '../pages/PayrollOverview';
import PaySchedules from '../pages/PaySchedules';
import PayrollDrafts from '../pages/PayrollDrafts';
import PayrollGuide from '../pages/PayrollGuide';
import PayrollSettings from '../pages/PayrollSettings';
import BankAccounts from '../pages/BankAccounts';
import ConnectBankFlow from '../pages/ConnectBankFlow';
import PaychequeList from '../pages/PaychequeList';
import PaychequeDetail from '../pages/PaychequeDetail';
import RunPayroll from '../pages/RunPayroll';
import PayrollPreview from '../pages/PayrollPreview';
import PayrollDone from '../pages/PayrollDone';
import PayRuns from '../pages/PayRuns';
import PayrollLauncher from '../utils/payrollLauncher';

import SalesLayout from '../pages/Sales/SalesLayout';
import SalesOverview, { SalesOverviewHeader } from '../pages/Sales/Overview';
import InvoicesList from '../pages/Sales/Invoices/List';
import PaymentLinks from '../pages/Sales/PaymentLinks';
import Payouts from '../pages/Sales/Payouts';
import Products from '../pages/Sales/Products';
import ChaseHub from '../pages/Sales/Chase';

import SalesTaxOverview, { SalesTaxOverviewHeader } from '../pages/SalesTax/Overview';

import AccountingOverview, { AccountingOverviewHeader } from '../pages/Accounting/Overview';
import ChartOfAccountsPage, { ChartOfAccountsHeader } from '../pages/Accounting/ChartOfAccounts';
import AccountDrillPage, { ChartOfAccountsDrillHeader } from '../pages/Accounting/ChartOfAccounts/AccountDrillPage';
import BankAccountHistoryPage from '../pages/Accounting/ChartOfAccounts/bank-history';
import RunReportPage from '../pages/Accounting/ChartOfAccounts/run-report';
import CleanupHistoryPage, { CleanupHistoryHeader } from '../pages/Accounting/ChartOfAccounts/cleanup-history';
import EditModePage, { EditModeHeader } from '../pages/Accounting/ChartOfAccounts/edit';
import CreateMultiplePage, { CreateMultipleHeader } from '../pages/Accounting/ChartOfAccounts/create-multiple';
import CleanupReviewPage, { ReviewPageHeader } from '../pages/Accounting/ChartOfAccounts/cleanup-review';
import BankTransactionsPage, { BankTransactionsHeader } from '../pages/Accounting/BankTransactions';
import ReceiptsPage, { ReceiptsHeader } from '../pages/Accounting/Receipts';
import ReconcilePage, { ReconcileHeader } from '../pages/Accounting/Reconcile';
import ReconcileSessionPage from '../pages/Accounting/Reconcile/Session';
import ReconcileHistoryPage from '../pages/Accounting/Reconcile/History';
import ReconcileSummaryPage from '../pages/Accounting/Reconcile/Summary';
import IntegrationTransactionsPage, { IntegrationTransactionsHeader } from '../pages/Accounting/IntegrationTransactions';
import RulesPage, { RulesHeader } from '../pages/Accounting/Rules';
import RecurringPage, { RecurringHeader } from '../pages/Accounting/Recurring';
import MyAccountantPage, { MyAccountantHeader } from '../pages/Accounting/MyAccountant';
import InvitePage from '../pages/Accounting/MyAccountant/InvitePage';
import FindAccountantPage, { FindAccountantHeader } from '../pages/Accounting/MyAccountant/findAccountant';
import AccountantProfilePage, { AccountantProfileHeader } from '../pages/Accounting/MyAccountant/findAccountant/components/AccountantProfilePage';
import AccountListReportPage, { AccountListReportHeader } from '../pages/Accounting/Reports/AccountList';

import InvoiceEditor from '../pages/InvoiceEditor';
import InvoiceReviewSend from '../pages/InvoiceReviewSend';
import Invoices from '../pages/Invoices';
import InvoicesAll from '../pages/InvoicesAll';

import Dashboard from '../pages/Dashboard';
import SettingsCompany from '../pages/SettingsCompany';
import AccountBilling from '../pages/AccountBilling';
import SubscriptionExpired from '../pages/SubscriptionExpired';
import Documents from '../pages/Documents';
import Transactions from '../pages/Transactions';
import Budgets from '../pages/Budgets';
import TaxCalculator from '../pages/TaxCalculator';
import VendorAnalytics from '../pages/VendorAnalytics';
import Currency from '../pages/Currency';
import ReceiptScanner from '../pages/ReceiptScanner';
import Agents from '../pages/Agents';
import Team from '../pages/Team';
import FinancialReports from '../pages/FinancialReports';
import ReportsLayout from '../layouts/ReportsLayout';
import CashFlowOverview from '../pages/Reports/CashFlowOverview';
import Reconciliation from '../pages/Reconciliation';
import BillPay from '../pages/BillPay';
import VarianceReports from '../pages/VarianceReports';
import LedgerView from '../pages/LedgerView';
import DocumentComparison from '../pages/DocumentComparison';
import Settings from '../pages/Settings';
import Help from '../pages/Help';
import SmartSearch from '../pages/SmartSearch';
import Customers from '../pages/Customers';
import Inventory from '../pages/Inventory';
import APIAccess from '../pages/APIAccess';
import Businesses from '../pages/Businesses';
import VelaInboxPage from '../pages/VelaInbox';

function ReportsPlaceholder({ title }: { title: string }): React.ReactElement {
  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0F172A', margin: '0 0 2px' }}>{title}</h2>
      <div style={{ fontSize: 11, color: '#64748B', margin: '0 0 24px' }}>Coming soon</div>
      <div style={{ background: '#fff', border: '1px solid #E7EAF0', borderRadius: 14, padding: 40, textAlign: 'center', color: '#94A3B8', fontSize: 13, fontStyle: 'italic' }}>
        This report is being built. In the meantime, use the sidebar to navigate to other reports.
      </div>
    </div>
  );
}

const PANEL_PATHS = new Set<string>([
  '/invoices', '/invoices/list', '/invoices/payment-links', '/invoices/payouts', '/invoices/products', '/invoices/chase',
  '/tax', '/tax/rates', '/tax/filings', '/tax/calculator',
  '/accounting', '/accounting/bank-transactions', '/accounting/receipts', '/accounting/reconcile', '/accounting/rules',
  '/accounting/chart-of-accounts',
  '/accounting/recurring', '/accounting/my-accountant', '/accounting/connections', '/accounting/integration-transactions',
]);

function shouldShowAllAppsPanel(pathname: string): boolean {
  return PANEL_PATHS.has(pathname);
}

function pageHeaderForPath(pathname: string): React.ReactNode | undefined {
  if (pathname === '/invoices') return <SalesOverviewHeader />;
  if (pathname === '/tax') return <SalesTaxOverviewHeader />;
  if (pathname === '/accounting') return <AccountingOverviewHeader />;
  if (pathname === '/accounting/chart-of-accounts') return <ChartOfAccountsHeader />;
  if (pathname === '/accounting/chart-of-accounts/cleanup-history') return <CleanupHistoryHeader />;
  if (pathname === '/accounting/chart-of-accounts/edit') return <EditModeHeader />;
  if (pathname === '/accounting/chart-of-accounts/create-multiple') return <CreateMultipleHeader />;
  if (pathname === '/accounting/chart-of-accounts/review-archive') return <ReviewPageHeader kind="archive" />;
  if (pathname === '/accounting/chart-of-accounts/review-merge') return <ReviewPageHeader kind="merge" />;
  if (pathname === '/accounting/chart-of-accounts/review-categorize') return <ReviewPageHeader kind="categorize" />;
  if (pathname === '/accounting/reports/account-list') return <AccountListReportHeader />;
  if (pathname.match(/^\/accounting\/chart-of-accounts\/[^/]+\/(history|report)$/)) return undefined;
  if (pathname.startsWith('/accounting/chart-of-accounts/')) return <ChartOfAccountsDrillHeader />;
  if (pathname === '/accounting/bank-transactions') return <BankTransactionsHeader />;
  if (pathname === '/accounting/receipts') return <ReceiptsHeader />;
  if (pathname === '/accounting/reconcile') return <ReconcileHeader />;
  if (pathname === '/accounting/integration-transactions') return <IntegrationTransactionsHeader />;
  if (pathname === '/accounting/rules') return <RulesHeader />;
  if (pathname === '/accounting/recurring') return <RecurringHeader />;
  if (pathname === '/accounting/my-accountant') return <MyAccountantHeader />;
  if (pathname === '/accounting/my-accountant/find') return <FindAccountantHeader />;
  if (pathname.startsWith('/accounting/my-accountant/find/')) return <AccountantProfileHeader />;
  return undefined;
}

function LayoutShell(): React.ReactElement {
  const location = useLocation();
  const panel = shouldShowAllAppsPanel(location.pathname) ? <AllAppsLayout /> : undefined;
  const pageHeader = pageHeaderForPath(location.pathname);

  return (
    <AppLayout panel={panel} pageHeader={pageHeader}>
      <AccessGate />
      <Routes>
        <Route path="/payroll/taxes" element={<PayrollTaxes />} />
        <Route path="/payroll/taxes/payments" element={<PayrollTaxes />} />
        <Route path="/payroll/taxes/filings" element={<PayrollTaxes />} />
        <Route path="/payroll/taxes/archived" element={<ArchivedForms />} />
        <Route path="/payroll/taxes/archived/:id" element={<PD7AWorksheet />} />
        <Route path="/payroll/payments/history" element={<PaymentHistory />} />
        <Route path="/payroll/audit-log" element={<AuditLog />} />
        <Route path="/payroll/taxes/print" element={<PayrollTaxPrintView />} />
        <Route path="/payroll/taxes/t4-preview/employer" element={<T4EmployerSlips />} />
        <Route path="/payroll/taxes/t4-preview/summary" element={<T4Summary />} />
        <Route path="/payroll/taxes/t4-preview/employee" element={<T4EmployeeSlips />} />
        <Route path="/payroll/bank-accounts" element={<BankAccounts />} />
        <Route path="/payroll/compliance" element={<Compliance />} />
        <Route path="/payroll/employees" element={<EmployeesList />} />
        <Route path="/payroll/employees/add" element={<AddEmployee />} />
        <Route path="/payroll/employees/directory" element={<EmployeesDirectoryPage />} />
        <Route path="/payroll/employees/:id/v2" element={<EmployeeProfileV2 />} />
        <Route path="/payroll/employees/:id" element={<EmployeeProfileV2 />} />
        <Route path="/payroll/run" element={<PayrollLauncher />} />
        <Route path="/payroll/run/:payRunId" element={<RunPayroll />} />
        <Route path="/payroll/run/:payRunId/preview" element={<PayrollPreview />} />
        <Route path="/payroll/run/:payRunId/done" element={<PayrollDone />} />
        <Route path="/payroll/runs/new" element={<PayrollLauncher />} />
        <Route path="/payroll/runs/:runId/stubs/:stubId" element={<PayStubDetail />} />
        <Route path="/payroll/runs/:id" element={<PayRunDetail />} />
        <Route path="/payroll/runs" element={<PayRuns />} />
        <Route path="/payroll/overview" element={<PayrollOverview />} />
        <Route path="/payroll/schedules" element={<PaySchedules />} />
        <Route path="/payroll/drafts" element={<PayrollDrafts />} />
        <Route path="/payroll/guide" element={<PayrollGuide />} />
        <Route path="/payroll/settings" element={<PayrollSettings />} />
        <Route path="/payroll/settings/:section" element={<PayrollSettings />} />
        <Route path="/payroll/bank/connect" element={<ConnectBankFlow />} />
        <Route path="/payroll/bank/connect/review" element={<ConnectBankFlow />} />
        <Route path="/payroll/bank/connect/submitted" element={<ConnectBankFlow />} />
        <Route path="/payroll/bank/verify" element={<ConnectBankFlow />} />
        <Route path="/payroll/paycheques" element={<PaychequeList />} />
        <Route path="/payroll/paycheques/:id" element={<PaychequeDetail />} />
        <Route path="/payroll/items" element={<PayrollItems />} />
        <Route path="/payroll/items/schedules/:id" element={<PaySchedule />} />
        <Route path="/payroll/items/locations/:id" element={<WorkLocation />} />
        <Route path="/payroll/*" element={<Navigate to="/payroll/employees" replace />} />
        <Route path="/tools/audit-log" element={<AuditLog />} />

        <Route path="/invoices/all" element={<InvoicesAll />} />
        <Route path="/invoices/new" element={<Invoices />} />
        <Route path="/invoices/:id/edit" element={<Invoices />} />
        <Route path="/invoices/:id/qb-edit" element={<InvoiceEditor />} />
        <Route path="/invoices/new-qb" element={<InvoiceEditor />} />
        <Route path="/invoices/:id/send" element={<InvoiceReviewSend />} />

        <Route path="/invoices" element={<SalesLayout />}>
          <Route index element={<SalesOverview />} />
          <Route path="list" element={<InvoicesList />} />
          <Route path="payment-links" element={<PaymentLinks />} />
          <Route path="payouts" element={<Payouts />} />
          <Route path="products" element={<Products />} />
          <Route path="chase" element={<ChaseHub />} />
        </Route>

        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/company" element={<SettingsCompany />} />
        <Route path="/account/billing" element={<AccountBilling />} />
        <Route path="/subscription/expired" element={<SubscriptionExpired />} />

        <Route path="/" element={<Dashboard />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/budgets" element={<Budgets />} />

        <Route path="/tax" element={<SalesTaxOverview />} />
        <Route path="/tax/calculator" element={<TaxCalculator />} />

        <Route path="/accounting" element={<AccountingOverview />} />
        <Route path="/accounting/chart-of-accounts" element={<ChartOfAccountsPage />} />
        <Route path="/accounting/chart-of-accounts/cleanup-history" element={<CleanupHistoryPage />} />
        <Route path="/accounting/chart-of-accounts/edit" element={<EditModePage />} />
        <Route path="/accounting/chart-of-accounts/create-multiple" element={<CreateMultiplePage />} />
        <Route path="/accounting/chart-of-accounts/review-archive" element={<CleanupReviewPage />} />
        <Route path="/accounting/chart-of-accounts/review-merge" element={<CleanupReviewPage />} />
        <Route path="/accounting/chart-of-accounts/review-categorize" element={<CleanupReviewPage />} />
        <Route path="/accounting/chart-of-accounts/:accountId/history" element={<BankAccountHistoryPage />} />
        <Route path="/accounting/chart-of-accounts/:accountId/report" element={<RunReportPage />} />
        <Route path="/accounting/chart-of-accounts/:accountId" element={<AccountDrillPage />} />
        <Route path="/accounting/bank-transactions" element={<BankTransactionsPage />} />
        <Route path="/accounting/receipts" element={<ReceiptsPage />} />
        <Route path="/accounting/reconcile" element={<ReconcilePage />} />
        <Route path="/accounting/reconcile/session" element={<ReconcileSessionPage />} />
        <Route path="/accounting/reconcile/history" element={<ReconcileHistoryPage />} />
        <Route path="/accounting/reconcile/summary" element={<ReconcileSummaryPage />} />
        <Route path="/accounting/integration-transactions" element={<IntegrationTransactionsPage />} />
        <Route path="/accounting/rules" element={<RulesPage />} />
        <Route path="/accounting/recurring" element={<RecurringPage />} />
        <Route path="/accounting/my-accountant" element={<MyAccountantPage />} />
        <Route path="/accounting/my-accountant/find" element={<FindAccountantPage />} />
        <Route path="/accounting/my-accountant/find/:id" element={<AccountantProfilePage />} />
        <Route path="/accounting/reports/account-list" element={<AccountListReportPage />} />

        <Route path="/vendors" element={<VendorAnalytics />} />
        <Route path="/currency" element={<Currency />} />
        <Route path="/receipts" element={<ReceiptScanner />} />
        <Route path="/agents" element={<Agents />} />
        <Route path="/team" element={<Team />} />
        <Route path="/integrations" element={<Navigate to="/accounting/integration-transactions" replace />} />
        <Route path="/reports" element={<ReportsLayout />}>
          <Route index element={<FinancialReports />} />
          <Route path="custom" element={<ReportsPlaceholder title="Custom reports" />} />
          <Route path="management" element={<ReportsPlaceholder title="Management reports" />} />
          <Route path="planning/cash-flow-overview" element={<CashFlowOverview />} />
          <Route path="planning/cash-flow-planner" element={<ReportsPlaceholder title="Cash flow planner" />} />
          <Route path="planning/budgets" element={<ReportsPlaceholder title="Budgets" />} />
        </Route>
        <Route path="/reconciliation" element={<Reconciliation />} />
        <Route path="/billpay" element={<BillPay />} />
        <Route path="/variance" element={<VarianceReports />} />
        <Route path="/ledger" element={<LedgerView />} />
        <Route path="/comparison" element={<DocumentComparison />} />
        <Route path="/help" element={<Help />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/api-access" element={<APIAccess />} />
        <Route path="/businesses" element={<Businesses />} />
        <Route path="/search" element={<SmartSearch />} />
        <Route path="/vela/inbox" element={<VelaInboxPage />} />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </AppLayout>
  );
}

export default function AuthedRoutes(): React.ReactElement {
  return (
    <Routes>
      <Route
        path="/accounting/my-accountant/invite"
        element={
          <>
            <AccessGate />
            <InvitePage />
          </>
        }
      />
      <Route path="*" element={<LayoutShell />} />
    </Routes>
  );
}